'use client';

import Image from '@/components/Image';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useCallback, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { Autoplay, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { usePinkLeopardTracking } from '../hooks/usePinkLeopardTracking';

interface PhotoWallProps {
  swiperPart: {
    title?: string;
    number?: string;
    bg_img?: string;
    word_arr?: Array<{
      title: string;
      description: string;
    }>;
    swiper_lists: Array<{
      image: string;
      shop_sku?: string;
    }>;
  };
  deviceType: EPlatform;
}

export default function PhotoWall({ swiperPart, deviceType }: PhotoWallProps) {
  const { trackPhotoWallExposure, trackPhotoWallClick } = usePinkLeopardTracking();
  const { navigate } = useAppNavigation();
  const swiperRef = useRef<any>(null);

  // 曝光状态管理 - 使用useRef避免重新渲染
  const exposedPhotos = useRef<Set<number>>(new Set());

  // 照片点击处理
  const handlePhotoClick = useCallback(
    (item: any, index: number, e?: React.MouseEvent) => {
      // 阻止事件冒泡，避免触发其他点击事件
      e?.preventDefault();
      e?.stopPropagation();

      trackPhotoWallClick('照片墙', index);

      if (item.shop_sku) {
        // 使用navigate方法跳转到商品详情页
        navigate(`/goods-detail/${item.shop_sku}`);
      }
    },
    [trackPhotoWallClick, navigate],
  );

  // 导航按钮点击处理
  const handleNavClick = useCallback(
    (direction: 'prev' | 'next', e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (swiperRef.current?.swiper) {
        // 暂停自动播放，避免冲突
        swiperRef.current.swiper.autoplay?.stop();

        if (direction === 'prev') {
          swiperRef.current.swiper.slidePrev();
        } else {
          swiperRef.current.swiper.slideNext();
        }

        // 2秒后重新启动自动播放
        setTimeout(() => {
          if (swiperRef.current?.swiper && swiperPart.swiper_lists.length > 1) {
            swiperRef.current.swiper.autoplay?.start();
          }
        }, 2000);
      }
    },
    [swiperPart.swiper_lists.length],
  );

  // 照片曝光组件 - 每张照片单独监听曝光
  const PhotoItem = ({
    item,
    index,
    children,
  }: {
    item: any;
    index: number;
    children: React.ReactNode;
  }) => {
    const { ref, inView } = useInView({
      threshold: 0.6, // 照片60%可见时触发曝光
      triggerOnce: true, // 性能优化：只触发一次
      rootMargin: '0px 0px -5% 0px', // 性能优化：提前5%触发
    });

    useEffect(() => {
      if (inView && !exposedPhotos.current.has(index)) {
        // 延迟上报，减少滚动时的性能冲击
        const timer = setTimeout(() => {
          trackPhotoWallExposure('照片墙', index);
          exposedPhotos.current.add(index);
        }, 200);

        return () => clearTimeout(timer);
      }
    }, [inView, item, index]);

    return <div ref={ref}>{children}</div>;
  };

  // PC端渲染
  if (deviceType === EPlatform.WEB) {
    return (
      <div
        className="photo-list-pc relative box-border"
        style={{
          padding: '2.08vw 5.21vw 3.125vw 5.21vw', // 对应 px2vw(40px) px2vw(100px) px2vw(60px) px2vw(100px)
        }}
      >
        {/* 标题区域 */}
        <div
          className="word-box text-center"
          style={{ marginBottom: '2.34vw' }} // 对应 px2vw(45px)
        >
          {swiperPart.title && (
            <h2
              className="leading-none"
              style={{
                fontSize: 'max(1.04vw, 12px)', // 对应 max(px2vw(20px), 12px)
                lineHeight: '1.875vw', // 对应 px2vw(36px)
              }}
              dangerouslySetInnerHTML={{ __html: swiperPart.title }}
            />
          )}
          {swiperPart.number && (
            <h3
              className="font-bb text-[#e1435d]"
              style={{
                fontSize: '4.17vw', // 对应 px2vw(80px)
                lineHeight: '80px', // 固定行高
                marginTop: '0.78vw', // 对应 px2vw(15px)
              }}
            >
              {swiperPart.number}
            </h3>
          )}
        </div>

        {/* 主要内容区域 */}
        <div className="swiper-wrap flex justify-between items-center">
          {/* 左侧文字列表 */}
          {swiperPart.word_arr && (
            <ul
              className="left-word"
              style={{ width: '23.44vw' }} // 对应 px2vw(450px)
            >
              {swiperPart.word_arr.map((item, index) => (
                <li
                  key={index}
                  style={{ marginBottom: '1.56vw' }} // 对应 px2vw(30px)
                >
                  <h4
                    className="font-bb"
                    style={{
                      marginBottom: '0.52vw', // 对应 px2vw(10px)
                      fontSize: '1.46vw', // 对应 px2vw(28px)
                    }}
                  >
                    {item.title}
                  </h4>
                  <p
                    className="text-gray-600"
                    style={{
                      fontSize: 'max(1.04vw, 12px)', // 对应 max(px2vw(20px), 12px)
                      lineHeight: '1.46vw', // 对应 px2vw(28px)
                    }}
                  >
                    {item.description}
                  </p>
                </li>
              ))}
            </ul>
          )}

          {/* 右侧轮播区域 */}
          <div
            className="right-swiper relative"
            style={{ width: '61.98vw' }} // 对应 px2vw(1190px)
          >
            {/* 背景装饰图 */}
            {swiperPart.bg_img && (
              <Image
                src={swiperPart.bg_img}
                alt=""
                className="absolute"
                style={{
                  top: '4.17vw', // 对应 px2vw(80px)
                  width: '16.77vw', // 对应 px2vw(322px)
                  height: 'auto',
                }}
                width={322}
                height={0}
                sizes="17vw"
              />
            )}

            {/* Swiper轮播 */}
            <div
              style={{
                marginLeft: '10.42vw', // 对应 px2vw(200px)
              }}
            >
              <Swiper
                ref={swiperRef}
                modules={[Autoplay, Navigation]}
                slidesPerView={4}
                slidesPerGroup={4}
                loop={swiperPart.swiper_lists.length > 4}
                autoplay={
                  swiperPart.swiper_lists.length > 4
                    ? {
                        delay: 3000,
                        stopOnLastSlide: false,
                        disableOnInteraction: false, // 改为false，手动控制
                        pauseOnMouseEnter: true,
                      }
                    : false
                }
                navigation={false}
                className="photo-swiper"
                style={{
                  padding: '3.65vw 0', // 对应 px2vw(70px) 0 - 直接给Swiper组件
                }}
              >
                {swiperPart.swiper_lists.map((item, index) => (
                  <SwiperSlide key={`${item.image}-${index}`} className="photo-item relative">
                    <PhotoItem item={item} index={index}>
                      <Image
                        src={item.image}
                        alt={`zeelool eyewear${index}`}
                        className={`cursor-pointer block
                                    ${index % 4 === 1 ? 'transform -rotate-[5deg] relative' : ''}
                                    ${index % 4 === 2 ? 'transform rotate-[15deg] relative' : ''}
                                    ${index % 4 === 3 ? 'transform -rotate-[5deg] relative' : ''}`}
                        style={{
                          width: '13.65vw !important', // 对应 px2vw(262px)
                          ...(index % 4 === 1 && {
                            bottom: '3.125vw', // 对应 px2vw(60px)
                            right: '0.26vw', // 对应 px2vw(5px)
                          }),
                          ...(index % 4 === 2 && {
                            bottom: '1.56vw', // 对应 px2vw(30px)
                            right: '1.56vw', // 对应 px2vw(30px)
                          }),
                          ...(index % 4 === 3 && {
                            bottom: '2.08vw', // 对应 px2vw(40px)
                            right: '1.56vw', // 对应 px2vw(30px)
                          }),
                        }}
                        onClick={(e) => handlePhotoClick(item, index, e)}
                        width={262}
                        height={0}
                        sizes="14vw"
                      />
                    </PhotoItem>
                  </SwiperSlide>
                ))}
              </Swiper>

              {/* 导航按钮 - 与Vue版本样式完全一致 */}
              {swiperPart.swiper_lists.length > 4 && (
                <>
                  <div
                    className="photo-prev absolute z-[2] cursor-pointer"
                    style={{
                      bottom: '13.54vw', // 对应 px2vw(260px)
                      left: '9.375vw', // 对应 px2vw(180px)
                      width: '2.92vw', // 对应 px2vw(56px)
                    }}
                    onClick={(e) => handleNavClick('prev', e)}
                  >
                    <Image
                      src="/activity/pearl_left.jpg"
                      alt=""
                      className="w-full h-auto"
                      width={56}
                      height={56}
                      sizes="3vw"
                      style={{ height: 'auto' }}
                    />
                  </div>
                  <div
                    className="photo-next absolute z-[2] cursor-pointer"
                    style={{
                      bottom: '13.54vw', // 对应 px2vw(260px)
                      right: '0',
                      width: '2.92vw', // 对应 px2vw(56px)
                    }}
                    onClick={(e) => handleNavClick('next', e)}
                  >
                    <Image
                      src="/activity/pearl_right.jpg"
                      alt=""
                      className="w-full h-auto"
                      width={56}
                      height={56}
                      sizes="3vw"
                      style={{ height: 'auto' }}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // WAP端渲染
  return (
    <div
      className="photo-list-wap pb-[20px] bg-[#f2f3f5]"
      onClick={(e) => {
        const target = e.target as HTMLElement;
        const indexStr = target.getAttribute('data-index');
        if (indexStr !== null) {
          const index = parseInt(indexStr, 10);
          const item = swiperPart.swiper_lists[index];
          if (item) {
            handlePhotoClick(item, index);
          }
        }
      }}
    >
      {/* 标题区域 */}
      <div className="word-box relative w-full pt-[23px] pb-[15px]">
        {swiperPart.title && (
          <h2
            className="w-[245px] text-[16px] text-[#282828] leading-[20px] mx-auto text-center"
            dangerouslySetInnerHTML={{ __html: swiperPart.title }}
          />
        )}
        {swiperPart.number && (
          <h3 className="font-bb text-[35px] text-[#e1435d] leading-[20px] mt-[15px] text-center">
            {swiperPart.number}
          </h3>
        )}
        {swiperPart.bg_img && (
          <Image
            src={swiperPart.bg_img}
            alt=""
            className="absolute right-0 top-[14px] w-[66px] h-auto"
            width={66}
            height={0}
            sizes="66px"
            style={{ height: 'auto' }}
          />
        )}
      </div>

      {/* Swiper轮播 */}
      <Swiper
        modules={[Autoplay]}
        spaceBetween={7}
        slidesPerView={2.8}
        slidesPerGroup={1}
        loop
        loopAdditionalSlides={2}
        slidesOffsetBefore={7}
        slidesOffsetAfter={7}
        autoplay={{
          delay: 3000,
          stopOnLastSlide: false,
          disableOnInteraction: true,
          pauseOnMouseEnter: true,
        }}
        className="photo-swiper"
      >
        {swiperPart.swiper_lists.map((item, index) => (
          <SwiperSlide key={`${item.image}-${index}`} className="photo-item">
            <PhotoItem item={item} index={index}>
              <Image
                src={item.image}
                alt={`zeelool eyewear${index}`}
                data-index={index}
                className="w-full cursor-pointer"
                onClick={() => handlePhotoClick(item, index)}
                width={0}
                height={0}
                sizes="50vw"
                style={{ width: '100%', height: 'auto' }}
              />
            </PhotoItem>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}
